/**
 * Shared client-side shade analyzer with optional server backing.
 *
 * Reads the palette from a [data-viva-shades] JSON script (and an optional
 * data-viva-api base URL). Exposes:
 *   VivaShade.rank(imageEl, onStatus?) -> Promise<[{code,name,img,d}]>  (top 3)
 *   VivaShade.render(ul, ranked)
 *
 * The result always comes from the server-side hair-segmentation match (async
 * job + status polling). A browser-only colour average would include skin,
 * clothing and background, so it must never be presented as a real match.
 * When the service is unavailable the caller receives an error and renders an
 * explicit unavailable state instead of fabricated shade recommendations.
 */
window.VivaShade = (function () {
  const srgbToLinear = (c) => {
    c /= 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  const rgbToLab = (r, g, b) => {
    const rl = srgbToLinear(r);
    const gl = srgbToLinear(g);
    const bl = srgbToLinear(b);
    let x = (rl * 0.4124 + gl * 0.3576 + bl * 0.1805) / 0.95047;
    let y = rl * 0.2126 + gl * 0.7152 + bl * 0.0722;
    let z = (rl * 0.0193 + gl * 0.1192 + bl * 0.9505) / 1.08883;
    const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
    x = f(x);
    y = f(y);
    z = f(z);
    return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  };

  const sampleRegion = (ctx, x0, y0, w, h) => {
    const { data } = ctx.getImageData(x0, y0, w, h);
    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (let i = 0; i < data.length; i += 4) {
      const pr = data[i];
      const pg = data[i + 1];
      const pb = data[i + 2];
      if (data[i + 3] < 200) continue;
      if (pr > 238 && pg > 238 && pb > 238) continue; // background
      r += pr;
      g += pg;
      b += pb;
      n += 1;
    }
    if (!n) return null;
    return [r / n, g / n, b / n];
  };

  const drawScaled = (img, max) => {
    const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const cw = Math.max(1, Math.round(img.naturalWidth * scale));
    const ch = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx) ctx.drawImage(img, 0, 0, cw, ch);
    return { canvas, ctx, cw, ch };
  };

  const colourFromImage = (img, region) => {
    const { ctx, cw, ch } = drawScaled(img, 220);
    if (!ctx) return null;
    if (region === 'hair') {
      const x0 = Math.round(cw * 0.28);
      const y0 = Math.round(ch * 0.06);
      const w = Math.max(1, Math.round(cw * 0.44));
      const h = Math.max(1, Math.round(ch * 0.4));
      return sampleRegion(ctx, x0, y0, w, h) || sampleRegion(ctx, 0, 0, cw, ch);
    }
    const x0 = Math.round(cw * 0.2);
    const y0 = Math.round(ch * 0.2);
    return sampleRegion(ctx, x0, y0, Math.round(cw * 0.6), Math.round(ch * 0.6));
  };

  const loadImage = (src) =>
    new Promise((resolve) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => resolve(im);
      im.onerror = () => resolve(null);
      im.src = src;
    });

  // --- Palette ---
  const dataEl = document.querySelector('[data-viva-shades]');
  const apiUrl = (dataEl && (dataEl.getAttribute('data-viva-api') || '').trim().replace(/\/$/, '')) || '';

  let shadesReady = Promise.resolve([]);
  if (dataEl) {
    let shades = [];
    try {
      shades = JSON.parse(dataEl.textContent);
    } catch (error) {
      shades = [];
    }
    shadesReady = Promise.all(
      shades.map(async (shade) => {
        // Prefer reading the real swatch colour from its image, but that needs a
        // CORS-clean canvas — fall back to the RGB embedded in the palette JSON
        // so a cross-origin/taint failure never empties the whole result.
        let rgb = null;
        const im = await loadImage(shade.img);
        if (im) {
          try {
            rgb = colourFromImage(im, 'swatch');
          } catch (error) {
            rgb = null;
          }
        }
        if (!rgb && Array.isArray(shade.rgb) && shade.rgb.length === 3) rgb = shade.rgb;
        return { ...shade, rgb, lab: rgb ? rgbToLab(rgb[0], rgb[1], rgb[2]) : null };
      })
    );
  }

  // fetch that aborts instead of hanging when the backend is unreachable.
  const fetchWithTimeout = (url, options, ms) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return fetch(url, Object.assign({ cache: 'no-store' }, options, { signal: controller.signal })).finally(() => clearTimeout(timer));
  };

  // Health gate — probe once, failing fast so we fall back without a long hang.
  const healthReady = apiUrl
    ? fetchWithTimeout(`${apiUrl}/health/ready`, { headers: { Accept: 'application/json' } }, 6000)
        .then((r) => r.ok)
        .catch(() => false)
    : Promise.resolve(false);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // --- Server-side match (real hair segmentation) ---
  const imageToBlob = (img) =>
    new Promise((resolve, reject) => {
      const { canvas, ctx } = drawScaled(img, 1024);
      if (!ctx) {
        reject(new Error('no-context'));
        return;
      }
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('no-blob'))), 'image/jpeg', 0.9);
    });

  const serverRank = async (imageEl, onStatus) => {
    const withData = await shadesReady;
    const palette = withData
      .filter((s) => s.rgb)
      .map((s) => ({ code: s.code, name: s.name, rgb: s.rgb.map((c) => Math.round(c)) }));
    if (!palette.length) throw new Error('no-palette');

    const blob = await imageToBlob(imageEl);
    const form = new FormData();
    form.append('source_image', blob, 'photo.jpg');
    form.append('palette', JSON.stringify(palette));

    if (onStatus) onStatus('queued', '');
    const created = await fetchWithTimeout(`${apiUrl}/api/public/shade-match/jobs`, { method: 'POST', body: form }, 20000);
    if (!created.ok) throw new Error('create-failed');
    const job = await created.json();
    const jobId = job.job_id;
    if (!jobId) throw new Error('no-job-id');

    const deadline = Date.now() + 30000;
    while (Date.now() < deadline) {
      await sleep(1200);
      const res = await fetchWithTimeout(`${apiUrl}/api/public/shade-match/jobs/${jobId}`, { headers: { Accept: 'application/json' } }, 7000);
      if (!res.ok) throw new Error('status-failed');
      const sj = await res.json();
      const state = sj.status && sj.status.state;
      if (onStatus) onStatus(state, '');
      if (state === 'succeeded') {
        const matches = (sj.output && sj.output.matches) || [];
        void fetchWithTimeout(`${apiUrl}/api/public/try-on/jobs/${jobId}/ack`, { method: 'POST', keepalive: true }, 7000).catch(() => {});
        return matches
          .map((m) => {
            const sh = withData.find((s) => s.code === m.code) || {};
            return { code: m.code, name: m.name || sh.name || '', img: sh.img, d: m.distance };
          })
          .slice(0, 3);
      }
      if (state === 'failed' || state === 'rejected') {
        void fetchWithTimeout(`${apiUrl}/api/public/try-on/jobs/${jobId}/ack`, { method: 'POST', keepalive: true }, 7000).catch(() => {});
        throw new Error(`job-${state}`);
      }
    }
    throw new Error('timeout');
  };

  const rank = async (imageEl, onStatus) => {
    if (!apiUrl || !(await healthReady)) {
      throw new Error('shade-service-unavailable');
    }
    return serverRank(imageEl, onStatus);
  };

  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const render = (ul, ranked, labels = {}) => {
    if (!ul) return;
    // Relative closeness within the result set (the metric scale differs between
    // the server and the in-browser estimate, so never show an absolute %).
    const dists = ranked.map((s) => (typeof s.d === 'number' && isFinite(s.d) ? Math.max(s.d, 0) : null));
    const valid = dists.filter((d) => d !== null);
    const best = valid.length ? Math.min.apply(null, valid) : null;

    ul.innerHTML = ranked
      .map((s, i) => {
        const swatchSource = String(s.img || '');
        const widthToken = /([?&])width=240(?=&|$)/;
        const responsiveSwatch = widthToken.test(swatchSource);
        const swatchAt = (width) => swatchSource.replace(widthToken, `$1width=${width}`);
        const swatchSrcset = responsiveSwatch
          ? ` srcset="${[48, 96, 144].map((width) => `${esc(swatchAt(width))} ${width}w`).join(', ')}" sizes="2.8rem"`
          : '';
        const tag = i === 0 ? labels.best_label : labels.alternative_label;
        const hasBar = best !== null && dists[i] !== null;
        const closeness = hasBar && dists[i] > 0 ? Math.max(0.18, Math.min(1, best / dists[i])) : 1;
        const bar = hasBar
          ? `<span class="viva-shade-result__bar" aria-hidden="true"><span style="width:${Math.round(closeness * 100)}%"></span></span>`
          : '';
        return `
        <li class="viva-shade-result${i === 0 ? ' is-best' : ''}" aria-label="${esc(tag)}: ${esc(labels.color_label)} ${esc(s.code)} — ${esc(s.name)}">
          <span class="viva-shade-result__swatch"><img src="${esc(responsiveSwatch ? swatchAt(48) : swatchSource)}"${swatchSrcset} alt="" width="48" height="48" loading="lazy"></span>
          <span class="viva-shade-result__meta">
            <span class="viva-shade-result__tag">${esc(tag)}</span>
            <span class="viva-shade-result__line">
              <span class="viva-shade-result__code">${esc(s.code)}</span>
              <span class="viva-shade-result__name">${esc(s.name)}</span>
            </span>
            ${bar}
          </span>
        </li>`;
      })
      .join('');
  };

  return { rank, render };
})();
